import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { type InfiniteData, useQuery, useQueryClient } from "@tanstack/react-query";
import TraceList from "./components/TraceList";
import TraceViewer from "./components/TraceViewer";
import ReportsView from "./components/ReportsView";
import {
  deleteTrace,
  exportAnnotationsRequest,
  fetchAnnotationSummary,
  fetchReportsOverview,
  listChannels,
  listFailureModes,
  triggerIngest
} from "./api";
import { Trace, TraceAnnotationCounts, TraceListResponse } from "./types";

interface Filters {
  severity?: string;
  failureMode?: string;
  startDate?: string;
  endDate?: string;
  channel?: string;
  reviewStatus?: string;
}

const createTodayFilters = (): Filters => {
  const today = format(new Date(), "yyyy-MM-dd");
  return {
    reviewStatus: "today",
    startDate: today,
    endDate: today
  };
};

export default function App() {
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(() => createTodayFilters());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [activePage, setActivePage] = useState<"traces" | "analytics">("traces");
  const [visibleTraces, setVisibleTraces] = useState<Trace[]>([]);
  const [deletingTraceId, setDeletingTraceId] = useState<string | null>(null);
  const [annotationCountsByTrace, setAnnotationCountsByTrace] = useState<Record<string, TraceAnnotationCounts>>({});
  const queryClient = useQueryClient();

  const { data: failureModesData } = useQuery({
    queryKey: ["failure-modes"],
    queryFn: () => listFailureModes()
  });

  const { data: channelsData } = useQuery({
    queryKey: ["channels"],
    queryFn: () => listChannels()
  });

  const { data: summaryData } = useQuery({
    queryKey: ["annotation-summary"],
    queryFn: () => fetchAnnotationSummary()
  });

  const reportsQuery = useQuery({
    queryKey: ["reports-overview"],
    queryFn: () => fetchReportsOverview(),
    enabled: activePage === "analytics"
  });

  const failureModes = failureModesData?.items ?? [];
  const channels = channelsData?.items ?? [];
  const annotationSummary = summaryData ?? [];

  const refreshTraces = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await triggerIngest();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["traces"] }),
      queryClient.invalidateQueries({ queryKey: ["annotation-summary"] })
    ]);
    setIsRefreshing(false);
  }, [isRefreshing, queryClient]);

  useEffect(() => {
    refreshTraces();
  }, [refreshTraces]);

  const handleFilterChange = (next: Filters) => {
    setFilters(next);
  };

  const handleTraceSelect = (id: string) => {
    setSelectedTraceId(id);
  };

  const doExport = async (overrides?: Partial<Filters>, mergeWithCurrent = true) => {
    if (isExporting) return;
    const base: Partial<Filters> = mergeWithCurrent ? { ...filters, ...(overrides ?? {}) } : { ...(overrides ?? {}) };
    const normalizedReviewStatus =
      base.reviewStatus && base.reviewStatus !== "today" ? base.reviewStatus : undefined;

    const exportFilters = {
      severity: base.severity,
      failureMode: base.failureMode,
      startDate: base.startDate,
      endDate: base.endDate,
      channel: base.channel,
      reviewStatus: normalizedReviewStatus
    };

    if (normalizedReviewStatus === "unannotated") {
      setToast({ message: "No annotations available in the unannotated queue.", tone: "info" });
      return;
    }

    try {
      setIsExporting(true);
      const { blob, filename } = await exportAnnotationsRequest(exportFilters, "csv");
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setToast({ message: "Annotations export ready.", tone: "success" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to export annotations.";
      setToast({ message, tone: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!toast) return;
    const handle = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(handle);
  }, [toast]);

  useEffect(() => {
    if (!visibleTraces.length) {
      if (selectedTraceId !== null) {
        setSelectedTraceId(null);
      }
      return;
    }
    if (!selectedTraceId || !visibleTraces.some((trace) => trace.id === selectedTraceId)) {
      setSelectedTraceId(visibleTraces[0].id);
    }
  }, [visibleTraces, selectedTraceId]);

  const currentTraceIndex = useMemo(() => {
    if (!selectedTraceId) return -1;
    return visibleTraces.findIndex((trace) => trace.id === selectedTraceId);
  }, [visibleTraces, selectedTraceId]);

  const goToRelativeTrace = useCallback(
    (direction: -1 | 1) => {
      if (currentTraceIndex === -1) return;
      const nextTrace = visibleTraces[currentTraceIndex + direction];
      if (nextTrace) {
        setSelectedTraceId(nextTrace.id);
      }
    },
    [currentTraceIndex, visibleTraces]
  );

  const handleDeleteTrace = useCallback(
    async (id: string) => {
      if (deletingTraceId) return;
      const proceed = window.confirm("Delete this trace forever? This action cannot be undone.");
      if (!proceed) return;
      setDeletingTraceId(id);
      try {
        await deleteTrace(id);
        setToast({ message: "Trace deleted.", tone: "success" });
        const existing = visibleTraces;
        const removedIndex = existing.findIndex((trace) => trace.id === id);
        const fallback = removedIndex === -1 ? existing[0] ?? null : existing[removedIndex + 1] ?? existing[removedIndex - 1] ?? null;
        setVisibleTraces((current) => current.filter((trace) => trace.id !== id));
        setAnnotationCountsByTrace((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        if (selectedTraceId === id) {
          setSelectedTraceId(fallback ? fallback.id : null);
        }

        queryClient.setQueriesData<InfiniteData<TraceListResponse>>({ queryKey: ["traces"] }, (data) => {
          if (!data) return data;
          return {
            pageParams: data.pageParams,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items?.filter((trace) => trace.id !== id) ?? []
            }))
          };
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["traces"] }),
          queryClient.invalidateQueries({ queryKey: ["annotation-summary"] })
        ]);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete trace.";
        setToast({ message, tone: "error" });
      } finally {
        setDeletingTraceId(null);
      }
    },
    [deletingTraceId, visibleTraces, selectedTraceId, queryClient]
  );

  const handleAnnotationSummaryChange = useCallback((traceId: string, summary: TraceAnnotationCounts | null) => {
    setAnnotationCountsByTrace((current) => {
      if (!summary) {
        if (current[traceId]) {
          const next = { ...current };
          delete next[traceId];
          return next;
        }
        return current;
      }
      if (
        current[traceId] &&
        current[traceId].total === summary.total &&
        current[traceId].needsAttention === summary.needsAttention &&
        current[traceId].resolved === summary.resolved &&
        current[traceId].positive === summary.positive &&
        current[traceId].informational === summary.informational
      ) {
        return current;
      }
      return {
        ...current,
        [traceId]: summary
      };
    });
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeTrace(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeTrace(1);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goToRelativeTrace]);

  const tracesPage = (
    <div className="traces-workspace">
      <section className="traces-workspace__viewer" aria-live="polite">
        {selectedTraceId ? (
          <TraceViewer
            traceId={selectedTraceId}
            onNavigatePrevious={() => goToRelativeTrace(-1)}
            onNavigateNext={() => goToRelativeTrace(1)}
            hasPrevious={currentTraceIndex > 0}
            hasNext={currentTraceIndex >= 0 && currentTraceIndex < visibleTraces.length - 1}
            onDeleteTrace={handleDeleteTrace}
            isDeletingTrace={deletingTraceId === selectedTraceId}
            onAnnotationSummaryChange={handleAnnotationSummaryChange}
          />
        ) : (
          <EmptyState />
        )}
      </section>
      <aside className="traces-workspace__list">
        <TraceList
          selectedTraceId={selectedTraceId}
          onSelect={handleTraceSelect}
          onDeleteTrace={handleDeleteTrace}
          isDeletingTrace={Boolean(deletingTraceId)}
          filters={filters}
          onFiltersChange={handleFilterChange}
          failureModes={failureModes}
          channels={channels}
          onRefresh={refreshTraces}
          isRefreshing={isRefreshing}
          onExport={() => doExport()}
          isExporting={isExporting}
          onTracesChange={setVisibleTraces}
          annotationCountsByTrace={annotationCountsByTrace}
        />
      </aside>
    </div>
  );

  const endDateIso = toIsoDate(new Date());
  const sevenDayStart = toIsoDate(shiftUtcDays(new Date(), -6));
  const thirtyDayStart = toIsoDate(shiftUtcDays(new Date(), -29));

  const analyticsPage = (
    <ReportsView
      data={reportsQuery.data}
      isLoading={reportsQuery.isLoading}
      isExporting={isExporting}
      onExportRange={(range) => doExport({ startDate: range.startDate, endDate: range.endDate }, false)}
      latestRange={{
        sevenDay: { startDate: sevenDayStart, endDate: endDateIso },
        thirtyDay: { startDate: thirtyDayStart, endDate: endDateIso }
      }}
      error={reportsQuery.error instanceof Error ? reportsQuery.error : reportsQuery.error ? new Error(String(reportsQuery.error)) : null}
    />
  );

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar">
        <div className="app-shell__brand">
          <h1>Trace Viewer</h1>
          <p>Inspect traces, add annotations, and monitor analytics.</p>
        </div>
        <nav className="app-shell__menu">
          <button
            type="button"
            className={activePage === "traces" ? "app-shell__menu-button app-shell__menu-button--active" : "app-shell__menu-button"}
            onClick={() => setActivePage("traces")}
          >
            Traces
          </button>
          <button
            type="button"
            className={activePage === "analytics" ? "app-shell__menu-button app-shell__menu-button--active" : "app-shell__menu-button"}
            onClick={() => setActivePage("analytics")}
          >
            Analytics
          </button>
        </nav>
        <div className="app-shell__sidebar-footer">
          <dl>
            <div>
              <dt>Total annotations</dt>
              <dd>{annotationSummary.reduce((total, row) => total + row.total, 0)}</dd>
            </div>
            <div>
              <dt>Filters active</dt>
              <dd>{Object.values(filters).filter(Boolean).length}</dd>
            </div>
          </dl>
        </div>
      </aside>
      <main className="app-shell__main">
        {activePage === "traces" ? tracesPage : analyticsPage}
      </main>
      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty-state">
      <h2>Select a trace</h2>
      <p>Choose a trace from the list to review messages, tool calls, and add annotations.</p>
    </div>
  );
}

interface ToastState {
  message: string;
  tone: "success" | "error" | "info";
}

function Toast({ message, tone, onDismiss }: { message: string; tone: ToastState["tone"]; onDismiss: () => void }) {
  return (
    <div className={`toast toast--${tone}`} role="status">
      <span>{message}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss notification">
        x
      </button>
    </div>
  );
}

function shiftUtcDays(base: Date, days: number): Date {
  const copy = new Date(base);
  copy.setUTCDate(copy.getUTCDate() + days);
  return copy;
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
